import React, { useState, useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { Table, TableBody, TableHead, TableCell, TableContainer, TableRow, Paper, Switch } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { updateLoading } from '../../../redux/features/common/commonSlice';
import ApiCaller from '../../constants/ApiCaller';
import { showToast, showDangerToast } from '../../constants/toaster';
import NavBar from '../../structure/NavBar';
import { NavBarTop } from '../../structure/NavBarTop';
import urls from '../../constants/apiurl';

const NewsCategoryList = () => {
    const [newsCategories, setNewsCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ categoryName: '', status: true });
    const [formErrors, setFormErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(false); // Track form validity
    const dispatch = useDispatch();
    const userData = useSelector(state => state?.userData);

    // Fetch news categories list
    const fetchNewsCategories = async () => {
        try {
            dispatch(updateLoading(true));
            const path = urls.newsCategoryList;
            const res = await ApiCaller.get({}, path, userData?.access_token);
            const response = await res.response.json();
            if (response.status === 200 || response.status === '200') {
                setNewsCategories(response.data);
            } else {
                showDangerToast(response.message);
            }
        } catch (error) {
            console.error(error);
            showDangerToast(error.message);
        } finally {
            dispatch(updateLoading(false));
        }
    };

    // Validate form fields
    const validateForm = () => {
        const errors = {};
        if (!formData.categoryName?.trim()) errors.categoryName = 'Name is required.';
        setFormErrors(errors);
        setIsFormValid(Object.keys(errors).length === 0);
    };

    // Trigger validation whenever formData changes
    useEffect(() => {
        validateForm();
    }, [formData]);

    // Handle add/edit form submission
    const handleSubmit = async () => {
        if (!isFormValid) return;

        try {
            dispatch(updateLoading(true));
            const path = urls.addNewsCategory
            const res = await ApiCaller.post(formData, path, userData?.access_token);
            const response = await res.response.json();
            if (response.status === 200 || response.status === '200') {
                showToast(response.message);
                fetchNewsCategories();
                setShowModal(false);
            } else {
                showDangerToast(response.message);
            }
        } catch (error) {
            console.error(error);
            showDangerToast(error.message);
        } finally {
            dispatch(updateLoading(false));
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            dispatch(updateLoading(true));
            const path = `${urls.newsCategoryDelete}/${id}`;
            const res = await ApiCaller.delete({}, path, userData?.access_token);
            const response = await res.response.json();
            if (response.status === 200) {
                showToast(response.message);
                fetchNewsCategories();
            } else {
                showDangerToast(response.message);
            }
        } catch (error) {
            console.error(error);
            showDangerToast(error.message);
        } finally {
            dispatch(updateLoading(false));
        }
    };

    // Handle status change
    const handleStatusChange = async (id, status) => {
        try {
            dispatch(updateLoading(true));
            const path = `${urls.newsCategoryStatusChange}/${id}`;
            const res = await ApiCaller.put({ status }, path, userData?.access_token);
            const response = await res.response.json();
            if (response.status === 200) {
                showToast(response.message);
                fetchNewsCategories();
            } else {
                showDangerToast(response.message);
            }
        } catch (error) {
            console.error(error);
            showDangerToast(error.message);
        } finally {
            dispatch(updateLoading(false));
        }
    };

    useEffect(() => {
        fetchNewsCategories();
    }, []);

    return (
        <>
            <NavBar />
            <div className="main-content">
                <NavBarTop />
                <div className="header">
                    <div className="logo">
                        <div className="header bg-gradient-primary pb-8 pt-5 pt-md-8"></div>
                    </div>
                </div>
                <div className="container-fluid mt--7">
                    <div className="row">
                        <div className="col text-left">
                            <div className="card shadow">
                                <div className="card-header border-0">
                                    <div className="row align-items-center text-left">
                                        <div className="col">
                                            <h3 className="mb-0">News Categories</h3>
                                        </div>
                                        <div className="col text-right">
                                            <Button
                                                className="btn btn-sm btn-primary text-white"
                                                onClick={() => {
                                                    setFormData({ name: '', status: true });
                                                    setFormErrors({});
                                                    setShowModal(true);
                                                }}
                                            >
                                                Add News Category
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <TableContainer component={Paper}>
                                        <Table aria-label="news categories table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {newsCategories.map((category) => (
                                                    <TableRow key={category.id}>
                                                        <TableCell>{category.categoryName}</TableCell>
                                                        <TableCell>
                                                            <Switch
                                                                checked={category.status}
                                                                onChange={(e) =>
                                                                    handleStatusChange(category.id, e.target.checked)
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="outlined"
                                                                onClick={() => {
                                                                    setFormData(category);
                                                                    setFormErrors({});
                                                                    setShowModal(true);
                                                                }}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                color="secondary"
                                                                onClick={() => handleDelete(category.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{formData.id ? 'Edit News Category' : 'Add News Category'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group>
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.categoryName}
                                    onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
                                    isInvalid={!!formErrors.categoryName}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.categoryName}</Form.Control.Feedback>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid}>
                            Save
                        </Button>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
};

export default NewsCategoryList;